import { FormCard } from "@components/forms/FormCard";
import { RadioGroupField } from "@components/forms/RadioGroupField";
import { TextField } from "@components/forms/TextField";
import { API_ENDPOINTS } from "@features/tickets/config/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "@lib/api/client";
import { useToast } from "@lib/hooks/useToast";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  AlignLeft,
  Info,
  Paperclip,
  Route as RouteIcon,
  Tag,
  Ticket,
} from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { ROUTES } from "../../config/constants";
import { type TicketFormData, ticketSchema } from "../../config/schemas";
import { CATEGORIE_TICKET, toTicketStatusOptions } from "../../config/statuses";
import { useCreateTicket, useUpdateTicket } from "../../hooks/useTicketMutations";
import { TicketAttachmentUpload } from "../TicketAttachmentUpload";
import { TicketRouteSelector } from "../TicketRouteSelector";
import { ControlledTextArea } from "./ControlledTextArea";
import { FormSection } from "./FormSection";

const CATEGORY_OPTIONS = toTicketStatusOptions(CATEGORIE_TICKET);

interface TicketFormProps {
  mode?: "create" | "edit";
  initialData?: Partial<TicketFormData> & { id?: number | string };
  onSuccess?: () => void;
}

function getDefaultValues(): TicketFormData {
  return {
    title: "",
    description: "",
    category: "bug_mineur",
    routeEntries: [{ route: "", routeTitle: "", apiEndpoint: "", errorMessage: "" }],
    attachments: [],
  };
}

function mapInitialDataToFormData(
  data: Partial<TicketFormData> & { id?: number | string },
): TicketFormData {
  if (!data) return getDefaultValues();
  return {
    title: data.title ?? "",
    description: data.description ?? "",
    category: data.category ?? "bug_mineur",
    routeEntries: data.routeEntries ?? [
      { route: "", routeTitle: "", apiEndpoint: "", errorMessage: "" },
    ],
    attachments: [],
  };
}

export function TicketForm({ mode = "create", initialData, onSuccess }: TicketFormProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const createTicket = useCreateTicket();
  const updateTicket = useUpdateTicket();
  const isEditMode = mode === "edit";
  const isPending = isEditMode ? updateTicket.isPending : createTicket.isPending;

  const form = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues: initialData ? mapInitialDataToFormData(initialData) : getDefaultValues(),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = form;

  const routeCount = (watch("routeEntries") ?? []).length;

  // Warn the user before leaving the page with unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const uploadAttachments = async (ticketId: number | string, files: File[]) => {
    let uploadErrors = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(API_ENDPOINTS.TICKET_ATTACHMENTS.UPLOAD(ticketId), formData);
      } catch {
        uploadErrors++;
      }
    }
    if (uploadErrors > 0) {
      toast.error(
        uploadErrors === files.length
          ? "Erreur lors de l'ajout des pièces jointes"
          : `${files.length - uploadErrors}/${files.length} pièce(s) jointe(s) ajoutée(s)`,
        { dedupeKey: "upload-attachment" },
      );
    }
  };

  const onSubmit = (data: TicketFormData) => {
    const filesToUpload = (data.attachments ?? []).filter(
      (item): item is File => item instanceof File,
    );

    if (isEditMode && initialData?.id) {
      const ticketId = initialData.id;
      updateTicket.mutate(
        {
          id: ticketId,
          data: {
            title: data.title,
            description: data.description,
            category: data.category,
          },
        },
        {
          onSuccess: async () => {
            if (filesToUpload.length > 0) {
              await uploadAttachments(ticketId, filesToUpload);
            }
            toast.success("Ticket mis à jour");
            if (onSuccess) onSuccess();
            else navigate({ to: ROUTES.TICKETS.LIST });
          },
          onError: (error: unknown) => {
            const msg =
              (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
              "Erreur lors de la sauvegarde";
            toast.error(msg);
          },
        },
      );
    } else {
      createTicket.mutate(
        {
          title: data.title,
          description: data.description,
          category: data.category,
        },
        {
          onSuccess: async (response) => {
            const ticketId = response.id;
            if (ticketId && filesToUpload.length > 0) {
              await uploadAttachments(ticketId, filesToUpload);
            }
            toast.success("Ticket créé avec succès");
            if (onSuccess) onSuccess();
            else navigate({ to: ROUTES.TICKETS.LIST });
          },
          onError: (error: unknown) => {
            const msg =
              (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
              "Erreur lors de la création";
            toast.error(msg);
          },
        },
      );
    }
  };

  const handleCancel = () => {
    navigate({ to: ROUTES.TICKETS.LIST });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <FormCard
        title={isEditMode ? "Modifier le ticket" : "Créer un ticket"}
        icon={Ticket}
        subtitle={
          isEditMode
            ? "Modifiez les informations du ticket"
            : "Signalez un problème ou demandez une amélioration"
        }
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        isPending={isPending}
        submitText={isEditMode ? "Enregistrer les modifications" : "Créer le ticket"}
        cancelText="Annuler"
        className="shadow-lg shadow-primary/5"
      >
        <div className="space-y-4">
          {/* 1. Titre + Catégorie */}
          <FormSection index={1} icon={Tag} title="Informations générales">
            <div className="space-y-4">
              <TextField
                name="title"
                control={control}
                label="Titre du ticket"
                required
                placeholder="Décrivez brièvement le problème..."
                error={errors.title?.message}
              />
              <RadioGroupField
                name="category"
                control={control}
                label="Catégorie"
                options={CATEGORY_OPTIONS}
                variant="chips"
                required
              />
            </div>
          </FormSection>

          {/* 2. Description */}
          <FormSection
            index={2}
            icon={AlignLeft}
            title="Description détaillée"
            hint="Étapes de reproduction, comportement attendu, comportement observé"
          >
            <ControlledTextArea
              name="description"
              control={control}
              placeholder="Expliquez le problème en détail : étapes pour reproduire, comportement attendu, comportement observé..."
              rows={6}
              error={errors.description?.message}
              maxLength={5000}
            />
          </FormSection>

          {/* 3. Routes affectées */}
          <FormSection
            index={3}
            icon={RouteIcon}
            title="Routes affectées"
            optional
            trailing={
              routeCount > 0 && (
                <span className="badge badge-outline badge-sm font-mono">{routeCount}</span>
              )
            }
          >
            <TicketRouteSelector control={control} watch={watch} setValue={setValue} />
          </FormSection>

          {/* 4. Fichiers */}
          <FormSection index={4} icon={Paperclip} title="Fichiers joints" optional>
            <TicketAttachmentUpload errors={errors} watch={watch} setValue={setValue} />
          </FormSection>
        </div>

        <div className="mt-4 flex items-center gap-2 text-[11px] text-base-content/50 pl-1">
          <Info className="h-3 w-3" />
          <span>Les champs marqués « optionnel » peuvent être complétés plus tard.</span>
          {isDirty && (
            <span className="badge badge-warning badge-sm gap-1 ml-auto">
              <AlertTriangle className="h-3 w-3" />
              Modifications non enregistrées
            </span>
          )}
        </div>
      </FormCard>
    </div>
  );
}
